import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import BatchUploader from './BatchUploader';
import { showToast } from './NotificationTray';
import { useUpload } from '../contexts/UploadContext';
import useUploadDialog from '../hooks/useUploadDialog';
import { getPrivateCacheScope, isCurrentPrivateCacheScope, subscribePrivateCacheScope } from '../utils/privateCacheScope';
import { cacheStrategy } from '../utils/cacheStrategy';

const SharedUploadContext = createContext(null);

// The controller belongs to the app shell. Route launchers only supply its
// initial destination and callbacks while their page remains mounted.
export function SharedUploadProvider({ children }) {
  const uploads = useUpload();
  const uploadsRef = useRef(uploads); uploadsRef.current = uploads;
  const [session, setSession] = useState(null);
  const [accountStopped, setAccountStopped] = useState(() => {
    try { return Boolean(sessionStorage.getItem('photos:upload-account-ended')); } catch { return false; }
  });
  const current = useRef(null);
  const nextId = useRef(0);
  const location = useLocation();
  const close = useCallback(() => {
    if (uploadsRef.current?.uploading) { uploadsRef.current.minimizePanel(); return; }
    const previous = current.current;
    current.current = null; setSession(null);
    uploadsRef.current?.dismissIndicator();
    previous?.callbacks?.onRequestClose?.();
  }, []);
  const open = useCallback((owner, props) => {
    if (current.current) {
      uploadsRef.current?.maximizePanel();
      // Never replace a selected/active session or its destination with another
      // launcher's files. Close the redundant route launcher, not the session.
      if (current.current.owner !== owner) props.onRequestClose?.();
      return;
    }
    const created = { id: ++nextId.current, owner, props, callbacks: props, lastInitialFiles: props.initialFiles, identity: getPrivateCacheScope() };
    current.current = created; setSession(created);
    uploadsRef.current?.maximizePanel();
  }, []);
  const detach = useCallback(owner => {
    if (current.current?.owner === owner) current.current.callbacks = null;
  }, []);
  const update = useCallback((owner, props) => {
    const active = current.current;
    if (active?.owner !== owner) return;
    active.callbacks = props;
    if (props.initialFiles === active.lastInitialFiles) return;
    active.lastInitialFiles = props.initialFiles;
    const state = uploadsRef.current;
    if (state?.uploading || state?.uploadedCount || state?.completedCount || state?.processingCount || state?.failedCount) {
      showToast('Upload already in progress', 'Finish or close this upload session before replacing its files.', 'info');
      return;
    }
    const updated = {...active, props: {...active.props, initialFiles: props.initialFiles}};
    current.current = updated; setSession(updated);
  }, []);
  const complete = useCallback(async counts => {
    const active = current.current;
    if (!active || !isCurrentPrivateCacheScope(active.identity)) return;
    const albumId = counts && Object.prototype.hasOwnProperty.call(counts, 'albumId')
      ? counts.albumId : active.props.albumId;
    // Full-release caches use the authenticated user/tenant scope, not plain user.id.
    if (active.identity.scope) {
      await cacheStrategy.invalidate('upload', {userId: active.identity.scope});
      if (albumId) await cacheStrategy.clear('albumImages', {id: albumId, userId: active.identity.scope});
    }
    if (current.current?.id !== active.id || !isCurrentPrivateCacheScope(active.identity)) return;
    active.callbacks?.onUploadComplete?.(counts);
    const detail = { scope: active.identity.scope, counts, detached: !active.callbacks?.onUploadComplete,
      albumId, archiveFolderId: active.props.archiveFolderId,
      vaultCollectionId: active.props.vaultCollectionId, libraryScope: active.props.libraryScope };
    window.dispatchEvent(new CustomEvent('photos:upload-completed', {detail}));
    if (!active.callbacks?.onUploadComplete) window.dispatchEvent(new CustomEvent('photos:mobile-upload-updated', {detail}));
  }, []);
  useEffect(() => subscribePrivateCacheScope(() => {
    if (!current.current || isCurrentPrivateCacheScope(current.current.identity)) return;
    if (uploadsRef.current?.uploading) {
      try { sessionStorage.setItem('photos:upload-account-ended', '1'); } catch {}
      setAccountStopped(true);
    }
    current.current = null; setSession(null);
    uploadsRef.current?.dismissIndicator();
  }), []);
  useEffect(() => {
    const beforeUnload = event => {
      if (!current.current || !uploadsRef.current?.uploading) return;
      event.preventDefault(); event.returnValue = '';
    };
    const beforeSignOut = event => {
      if (!current.current || !uploadsRef.current?.uploading) return;
      if (!window.confirm('Signing out stops uploads still transferring or confirming. Photos already received may continue processing. Sign out and stop this upload session?')) event.preventDefault();
    };
    window.addEventListener('beforeunload', beforeUnload);
    window.addEventListener('photos:before-signout', beforeSignOut);
    return () => { window.removeEventListener('beforeunload', beforeUnload); window.removeEventListener('photos:before-signout', beforeSignOut); };
  }, []);
  // Internal navigation keeps the same controller, but gets the panel out of
  // the way. Browser back/forward uses this same route-independent ownership.
  useEffect(() => { if (current.current) uploadsRef.current?.minimizePanel(); }, [location.key]);
  const visible = Boolean(session && !uploads?.panelMinimized);
  const dialogRef = useUploadDialog(visible, close);
  const title = session?.props.archiveFolderId ? 'Upload scans' : session?.props.vaultCollectionId ? 'Upload to Vault' : 'Upload Photos';
  return <SharedUploadContext.Provider value={{open, detach, update}}>
    {children}
    {accountStopped && createPortal(<div role="alert" className="ph-upload-session-notice">
      <strong>Upload session stopped</strong>
      <p>Your previous upload session stopped because your account changed. Photos already received may continue processing.</p>
      <button type="button" onClick={() => { setAccountStopped(false); try { sessionStorage.removeItem('photos:upload-account-ended'); } catch {} }}>Dismiss upload notice</button>
    </div>, document.body)}
    {session && createPortal(<div className="modal-overlay" style={{display: visible ? undefined : 'none', zIndex: 1100}} onClick={close}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} className="ph-upload-shell" onClick={event => event.stopPropagation()}>
        <BatchUploader key={session.id} {...session.props} onRequestClose={close} onUploadComplete={complete}/>
      </div>
    </div>, document.body)}
  </SharedUploadContext.Provider>;
}

export default function SharedUploadLauncher(props) {
  const shared = useContext(SharedUploadContext);
  const owner = useRef(Symbol('upload-launcher'));
  const latest = useRef(props); latest.current = props;
  const open = shared?.open, detach = shared?.detach, update = shared?.update;
  useLayoutEffect(() => {
    if (!open) return;
    open(owner.current, latest.current);
    return () => detach(owner.current);
  }, [open, detach]);
  useLayoutEffect(() => { update?.(owner.current, latest.current); });
  // Component fixtures and standalone consumers keep the existing controller.
  return shared ? null : <BatchUploader {...props}/>;
}
